import { useRef, useCallback } from 'react';
import { useLogger, useMeetingManager } from 'amazon-chime-sdk-component-library-react';
import { VideoInputDevice } from 'amazon-chime-sdk-js';
import { streamTracker } from '../utils/streamTracker';

interface UseVideoStreamManagerReturn {
  startVideoStream: (deviceId: string) => Promise<void>;
  stopVideoStream: () => Promise<void>;
  isVideoStreamActive: boolean;
}

export const useVideoStreamManager = (): UseVideoStreamManagerReturn => {
  const logger = useLogger();
  const meetingManager = useMeetingManager();
  const currentStreamRef = useRef<MediaStream | null>(null);
  const isVideoStreamActiveRef = useRef<boolean>(false);

  const startVideoStream = useCallback(async (deviceId: string): Promise<void> => {
    try {
      // Stop any existing stream first
      if (currentStreamRef.current) {
        currentStreamRef.current.getTracks().forEach(track => track.stop());
        currentStreamRef.current = null;
      }

      // Create a new MediaStream with the specified device
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: deviceId },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      // Store the stream reference
      currentStreamRef.current = stream;
      isVideoStreamActiveRef.current = true;
      
      // Track the stream for cleanup
      streamTracker.addStream(stream);

      // Start the video input with the stream
      await meetingManager.audioVideo.startVideoInput(deviceId);

      logger.info('Video stream started successfully');
    } catch (error) {
      logger.error('Failed to start video stream:', error);
      throw error;
    }
  }, [meetingManager, logger]);

  const stopVideoStream = useCallback(async (): Promise<void> => {
    try {
      // Stop the Amazon Chime SDK video input
      await meetingManager.audioVideo.stopVideoInput();

      // Stop all tracks in our managed stream
      if (currentStreamRef.current) {
        // Remove from tracker first
        streamTracker.removeStream(currentStreamRef.current);
        
        currentStreamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        currentStreamRef.current = null;
      }

      isVideoStreamActiveRef.current = false;

      logger.info('Video stream stopped successfully');
    } catch (error) {
      logger.error('Failed to stop video stream:', error);
      throw error;
    }
  }, [meetingManager, logger]);

  return {
    startVideoStream,
    stopVideoStream,
    isVideoStreamActive: isVideoStreamActiveRef.current,
  };
};
